import { useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import useRegionAwareOfferList from 'hooks/Offers/useRegionAwareOfferList'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import config from 'constants/config'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import CurrencyContext from 'contexts/currencyContext'
import LuxPlusMonthTakeoverToursBenefitModal from './LuxPlusMonthTakeoverToursBenefitModal'
import { LUX_PLUS_MONTH_CAMPAIGN_ID, LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY } from '../constants/luxPlusMonthTakeoverConstants'

function LuxPlusMonthTakeoverTourOffersCarousel() {
  const renderItalic = useCallback((chunks: ReactNode) => <i>{chunks}</i>, [])
  const showModal = useContext(ModalContext)
  const currency = useContext(CurrencyContext)
  const tiers = LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY[currency] ?? LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY.AUD
  const maxVoucherAmount = tiers[tiers.length - 1].voucher

  const baseFilters = useMemo<Omit<App.OfferListFilters, 'destinationId' | 'limit'>>(() => ({
    offerTypes: ['tour_v2'],
    campaigns: [LUX_PLUS_MONTH_CAMPAIGN_ID],
  }), [])

  // No backup request here — region and anywhere share the same filters, campaign tag included.
  const regionRequest = useMemo(() => ({ filters: baseFilters, limit: 5 }), [baseFilters])
  const anywhereRequest = useMemo(() => ({ filters: baseFilters, limit: 15 }), [baseFilters])
  const offerList = useRegionAwareOfferList(regionRequest, anywhereRequest)

  const searchQuery = useMemo(() => encodeSearchParams({
    searchItem: ANYWHERE_SEARCH_ITEM,
    rooms: [config.search.defaultOccupants],
    filters: baseFilters,
  }), [baseFilters])

  const toursSearchLink = `/search/tours?${searchQuery.toString()}`

  const onShowToursBenefitModal = useCallback(() => {
    showModal(<LuxPlusMonthTakeoverToursBenefitModal searchLink={toursSearchLink} />)
  }, [showModal, toursSearchLink])

  return (
    <OfferCardCarouselSection
      id="tours"
      title={<FormattedMessage
        id="r64aryUUK7xnvvo3JyE6"
        defaultMessage="<i>Save more</i> on tour optional extras"
        description="Section title for the LuxPlus+ Month campaign landing page tour offers carousel, with 'Save more' shown in italics"
        values={{ i: renderItalic }}
      />}
      subtitle={<FormattedMessage
        id="ElhgcA-kL0uAsC2Vp84X"
        defaultMessage="Book an eligible tour and get up to {amount} off optional extras. <link>Learn more</link>"
        description="Description below the LuxPlus+ Month campaign landing page tour offers carousel title, with a 'Learn more' link opening a modal explaining the tour optional extras voucher benefit"
        values={{
          amount: <FormatCurrency value={maxVoucherAmount} format="roundedDollar" />,
          link: (chunks: ReactNode) => <TextLink size="large" weight="regular" onClick={onShowToursBenefitModal}>{chunks}</TextLink>,
        }}
      />}
      filters={baseFilters}
      overrideOfferList={offerList}
      allLink={toursSearchLink}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
  )
}

export default LuxPlusMonthTakeoverTourOffersCarousel
